import type { ReactNode } from "react";
import { usePreferencesStore } from "@/store/preferences-store";

/**
 * The vNext token scope for a surface Radix renders through a portal.
 *
 * `vnext.css` remaps the Tailwind / MD3 variables INSIDE `.dt-vnext` (spec
 * 2026-09-06 vNext page migration, D2), and `MainLayout` puts one such scope
 * around the app. A portal escapes it: `DialogPortal`, `DropdownMenuPortal`
 * and `SelectPortal` all mount into `document.body`, outside the shell's
 * subtree, so a dialog opened from a vNext page resolved `--primary`,
 * `--border`, `--popover` … from the LEGACY `:root` values in `index.css` —
 * two blues on one screen, and a light dialog over a dark shell.
 *
 * The scope therefore travels with the portal. Three properties are load-
 * bearing and each is pinned by `__tests__/vnext-portal-scope.test.tsx`:
 *
 * - **A wrapper, not the surface itself.** `.dt-vnext` sets `background`,
 *   which `vnext.css` is imported unlayered to do, so it beats every Tailwind
 *   utility (layered) on the SAME element. Putting the class on
 *   `SelectContent` (`bg-popover`, the panel white that lifts a menu off the
 *   page) would repaint it as the page background. A wrapper only cascades:
 *   the surface keeps its own fill.
 * - **`contents`.** The wrapper is a cascade node, never a box — no stray
 *   block in `<body>`, nothing painted, no effect on the `position: fixed`
 *   surfaces inside it.
 * - **`dt-app-shell`.** Three rules in `vnext.css` are gallery-only and are
 *   written as `.dt-vnext:not(.dt-app-shell)` — `svg { width: 20px }` above
 *   all, which would resize every `h-4 w-4` icon in a menu. A portal of the
 *   shell is the shell, so it carries the class; `min-height: 100vh` comes
 *   with it and is inert on a `display: contents` element, which is the
 *   second reason `contents` is not decoration.
 */
export const VNEXT_PORTAL_SCOPE = "contents dt-vnext dt-app-shell";

export interface VNextPortalScopeProps {
  children: ReactNode;
}

export function VNextPortalScope({ children }: VNextPortalScopeProps) {
  const theme = usePreferencesStore(state => state.theme);
  return <div className={VNEXT_PORTAL_SCOPE} data-theme={theme === "system" ? undefined : theme}>{children}</div>;
}
